// ============================================================================
// PARTNER MASTER SCREEN — FSA Partners master (NCP-13/14)
// ============================================================================
// Lists fsa.tbl_partner. Per-client (firm) roster — the Owners' Capital
// Account Note auto-fills partner rows + Share % from here, and the
// signature panel pulls partners flagged is_signatory.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useFsaPartners } from 'hooks/api/fsa/useFsaPartners';
import { fsaPartnerService } from 'api/services/fsa/fsaPartnerService';
import { clientService } from 'api/services/clientService';
import type { Partner, PartnerSearchRequest } from 'api/types/fsa/partner.types';
import PartnerFormModal from 'components/modals/modules/fsa/PartnerFormModal';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

const FsaPartnerMasterScreen: React.FC = () => {
  const [queryParams, setQueryParams] = useState<PartnerSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useFsaPartners(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState<string>('');
  const [nameFilter, setNameFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      clientId:        clientFilter || undefined,
      partnerName:     nameFilter || undefined,
      designation:     designationFilter || undefined,
      statusCode:      statusFilter || undefined,
      includeInactive: statusFilter === 'INACTIVE',
      page:            1,
    }));
    setFilterOpen(false);
  }, [clientFilter, nameFilter, designationFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setClientFilter('');
    setNameFilter('');
    setDesignationFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({
      ...prev,
      clientId: undefined, partnerName: undefined, designation: undefined, statusCode: undefined, includeInactive: undefined, page: 1,
    }));
  }, []);

  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    return [{ value: '', label: 'All firms' }, ...resp.data.map(c => ({
      value: String(c.id),
      label: c.legalName ? `${c.legalName} (${c.code})` : `${c.name} (${c.code})`,
    }))];
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns: SyutGridColumn<Partner>[] = useMemo(() => [
    { id: 'clientLegalName', header: 'Firm', accessorKey: 'clientLegalName', enableSorting: true, size: 240 },
    { id: 'partnerName', header: 'Partner', accessorKey: 'partnerName', enableSorting: true, size: 200 },
    { id: 'designation', header: 'Designation', accessorKey: 'designation', enableSorting: true, size: 150 },
    { id: 'pan', header: 'PAN', accessorKey: 'pan', enableSorting: false, size: 110 },
    { id: 'profitSharingRatio', header: 'Share %',
      accessorFn: (row: Partner) => row.profitSharingRatio,
      cell: ({ row }: { row: { original: Partner } }) => (
        <span style={{ textAlign: 'right', display: 'block' }}>
          {row.original.profitSharingRatio != null ? `${row.original.profitSharingRatio}%` : '-'}
        </span>
      ),
      size: 90, enableSorting: true },
    { id: 'isSignatory', header: 'Signatory',
      accessorFn: (row: Partner) => row.isSignatory,
      cell: ({ row }: { row: { original: Partner } }) => (
        <span style={{ color: row.original.isSignatory ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {row.original.isSignatory ? 'Yes' : 'No'}
        </span>
      ),
      size: 90, enableSorting: true },
    { id: 'effectiveFrom', header: 'Effective From',
      accessorFn: (row: Partner) => row.effectiveFrom,
      cell: ({ row }: { row: { original: Partner } }) => <span>{formatDate(row.original.effectiveFrom)}</span>,
      size: 120, enableSorting: true },
    { id: 'effectiveTo', header: 'Effective To',
      accessorFn: (row: Partner) => row.effectiveTo,
      cell: ({ row }: { row: { original: Partner } }) => (
        <span style={{ color: row.original.effectiveTo ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {row.original.effectiveTo ? formatDate(row.original.effectiveTo) : 'Active'}
        </span>
      ),
      size: 120, enableSorting: true },
    { id: 'status', header: 'Status', accessorKey: 'statusName', enableSorting: true, size: 100 },
  ], []);

  const handleEdit = useCallback((row: Partner) => {
    setSelectedPartner(row);
    setFormMode('edit');
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((row: Partner) => {
    setSelectedPartner(row);
    setDeleteModalOpen(true);
  }, []);

  const getRowActions = useCallback((row: Partner): ActionMenuItem[] => [
    { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={16} />, onClick: () => handleEdit(row) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(row), variant: 'danger' },
  ], [handleEdit, handleDeleteClick]);

  const handleAdd = useCallback(() => {
    setSelectedPartner(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add',     icon: <IconSystem.add     size={16} />, label: 'Add',     onClick: handleAdd, variant: 'primary' },
    { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => refetch(), variant: 'icon' },
  ], [handleAdd, refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPartner) return;
    setIsDeleting(true);
    try {
      await fsaPartnerService.remove(selectedPartner.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedPartner.partnerName}" deleted successfully.`);
      setSelectedPartner(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Delete failed.', 6000);
    }
  }, [selectedPartner, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Partners"
        description="Per-firm roster of partners (PSR + signatory). Auto-fills the Owners' Capital Account Note and drives the partnership signature block."
        icon={<IconSystem.users size={20} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search partners…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={getRowActions}
              emptyMessage={loading ? 'Loading…' : 'No partners found'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Firm"
                  value={clientFilter}
                  onChange={(val) => setClientFilter((val as string) || '')}
                  isAsync
                  loadOptions={loadClientOptions}
                  defaultOptions
                  cacheOptions={false}
                  isClearable
                  size="sm"
                  placeholder="All firms"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Partner Name</label>
                <input type="text" className="form-input" value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Designation</label>
                <input type="text" className="form-input" value={designationFilter}
                  onChange={(e) => setDesignationFilter(e.target.value)}
                  placeholder="Filter by designation…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete partner "${selectedPartner?.partnerName}"? Existing engagement data that references this partner keeps its saved values, but new engagements won't pick them up.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <PartnerFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedPartner}
        onSaved={refetch}
        onClose={() => setFormModalOpen(false)}
      />
    </div>
  );
};

export default FsaPartnerMasterScreen;
